import type { NavigateFunction } from 'react-router-dom';

import { getPathTenantSlug, isDevGlobalAuthEnabled } from '../../shared/config/workspaceHost';
import type { WorkspaceSelection } from './types/workspace';

const isSameOriginPathWorkspaceRedirect = (redirectUrl: string): boolean => {
  try {
    const url = new URL(redirectUrl, window.location.origin);
    return url.origin === window.location.origin && getPathTenantSlug(url.pathname) !== null;
  } catch {
    return false;
  }
};

export const appendSetupQuery = (url: string): string => {
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set('setup', '1');
  return parsed.toString();
};

/**
 * Sends the browser to the selected workspace.
 *
 * Dedicated workspace hosts still get a cross-origin navigation. Shared-host workspaces stay on
 * this origin at `/t/{slug}/...`, which remounts the router with the new basename. Local
 * development without a path URL keeps redeeming the handoff code on the current origin.
 *
 * `startSetup` marks a workspace that was just created, so the handoff opens the setup guide.
 */
export const followWorkspaceSelection = (
  selection: WorkspaceSelection,
  navigate: NavigateFunction,
  options?: { startSetup?: boolean },
): void => {
  const redirectUrl = options?.startSetup ? appendSetupQuery(selection.redirectUrl) : selection.redirectUrl;

  if (isSameOriginPathWorkspaceRedirect(redirectUrl)) {
    window.location.assign(redirectUrl);
    return;
  }

  if (isDevGlobalAuthEnabled() && selection.handoffCode) {
    const query = new URLSearchParams({
      code: selection.handoffCode,
      tenantId: selection.tenantId,
    });
    if (options?.startSetup) {
      query.set('setup', '1');
    }
    navigate(`/login/handoff?${query.toString()}`, { replace: true });
    return;
  }

  window.location.assign(redirectUrl);
};
